const API_ERROR_KEYS = {
  'login required': 'apiError.loginRequired',
  forbidden: 'apiError.forbidden',
  'csrf token is missing': 'apiError.csrfTokenMissing',
  'csrf token is invalid': 'apiError.csrfTokenInvalid',
  'admin session required': 'apiError.adminSessionRequired',
  'enterprise session required': 'apiError.enterpriseSessionRequired',
  'invalid request body': 'apiError.invalidRequestBody',
  'file is required': 'apiError.fileRequired',
  'internal error': 'apiError.internalError',
  'bad request': 'apiError.badRequest',
  'channel code already exists': 'apiError.channelCodeAlreadyExists',

  'request body is required': 'apiError.requestBodyRequired',
  'valid email is required': 'apiError.validEmailRequired',
  'companyname, contactname, contacttitle, contactphone, email and verifycode are required':
    'apiError.registerFieldsRequired',
  'privacy agreement must be accepted': 'apiError.privacyAgreementRequired',
  'verification code is invalid or expired': 'apiError.verificationCodeInvalidOrExpired',
  'email already exists': 'apiError.emailAlreadyExists',
  'channel not found': 'apiError.channelNotFound',
  'register failed': 'apiError.registerFailed',
  'id is required': 'apiError.idRequired',
  'enterprise not found': 'apiError.enterpriseNotFound',
  'enterprise is not pending activation': 'apiError.enterpriseNotPendingActivation',
  'activation link is expired': 'apiError.activationLinkExpired',
  'login email is missing': 'apiError.loginEmailMissing',
  'id, verifycode and password are required': 'apiError.activateFieldsRequired',
  'activate failed': 'apiError.activateFailed',
  'account is required': 'apiError.accountRequired',
  'enterprise is not activated': 'apiError.enterpriseNotActivated',
  'enterprise account is not ready': 'apiError.enterpriseAccountNotReady',
  'account, verifycode and password are required': 'apiError.resetPasswordFieldsRequired',
  'reset password failed': 'apiError.resetPasswordFailed',
  'username and password are required': 'apiError.loginFieldsRequired',
  'username or password is invalid': 'apiError.usernameOrPasswordInvalid',
  'enterprise is deleted': 'apiError.enterpriseDeleted',
  'password must be at least 8 characters': 'apiError.passwordMinLength',
  'password must contain at least three of: uppercase, lowercase, digit, symbol':
    'apiError.passwordComplexity',
  'companyname is required': 'apiError.companyNameRequired',
  'contactname is required': 'apiError.contactNameRequired',
  'loginemail is required': 'apiError.loginEmailRequired',
  'valid loginemail is required': 'apiError.validLoginEmailRequired',
  'loginemail already exists': 'apiError.loginEmailAlreadyExists',
  'update profile failed': 'apiError.updateProfileFailed',
  'prooffileurl is required': 'apiError.proofFileUrlRequired',
  'prooffileurl must contain at least one file': 'apiError.proofFileUrlMinOne',
  'prooffileurl must be a json array of {prooffileurl, prooffilename}':
    'apiError.proofFileUrlJsonFormat',
  'each proof file must have prooffileurl': 'apiError.proofFileUrlRequiredEach',

  'enterpriseid is required': 'apiError.enterpriseIdRequired',
  'enterpriseid does not match current login': 'apiError.enterpriseIdMismatch',
  'status must be 1 (draft) or 2 (submitted)': 'apiError.surveyStatusInvalid',
  'answers is required': 'apiError.answersRequired',
  'questionid is required': 'apiError.questionIdRequired',
  'save answers failed': 'apiError.saveAnswersFailed',
  'update survey progress failed': 'apiError.updateSurveyProgressFailed',

  'base64 is required': 'apiError.base64Required',
  'file size must be <= 10mb': 'apiError.fileSizeLimit',
  'only jpg / png / pdf files are supported': 'apiError.onlyJpgPngPdf',
  'only png or jpg images are supported': 'apiError.onlyPngJpg',
  'invalid base64 image format': 'apiError.invalidBase64ImageFormat',
  'invalid base64 image content': 'apiError.invalidBase64ImageContent',
  'image cannot be compressed below 1mb': 'apiError.imageCompressFailed',
  'invalid upload target': 'apiError.invalidUploadTarget',
  'save image failed': 'apiError.saveImageFailed',
  'save file failed': 'apiError.saveFileFailed',
  'read file failed': 'apiError.readFileFailed',
  'read image failed': 'apiError.readImageFailed',
  'jpg writer unavailable': 'apiError.jpgWriterUnavailable',
  'compress image failed': 'apiError.compressImageFailed',

  'recipient email is required': 'apiError.recipientEmailRequired',
  'user email is required': 'apiError.userEmailRequired',
};

const API_ERROR_PATTERNS = [
  {
    pattern: /^too many failed attempts, try again in (\d+) minutes$/,
    key: 'apiError.tooManyFailedAttempts',
    params: ([, minutes]) => ({ minutes }),
  },
  {
    pattern: /^duplicate questionid: (.+)$/,
    key: 'apiError.duplicateQuestionId',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^question not found: (.+)$/,
    key: 'apiError.questionNotFound',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^optionid is required for question (.+)$/,
    key: 'apiError.optionIdRequired',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^invalid optionid for question (.+)$/,
    key: 'apiError.invalidOptionId',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^textanswer is required for question (.+)$/,
    key: 'apiError.textAnswerRequired',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^unsupported questiontype for question (.+)$/,
    key: 'apiError.unsupportedQuestionType',
    params: ([, id]) => ({ id }),
  },
  {
    pattern: /^email send failed: (.+)$/,
    key: 'apiError.emailSendFailed',
    params: ([, detail]) => ({ detail }),
  },
  {
    pattern: /^email service connection failed: (.+)$/,
    key: 'apiError.emailServiceConnectionFailed',
    params: ([, detail]) => ({ detail }),
  },
];

function normalizeMessage(message) {
  return message?.trim().toLowerCase() ?? '';
}

function resolveApiError(message) {
  const normalized = normalizeMessage(message);
  if (!normalized) return null;

  const key = API_ERROR_KEYS[normalized];
  if (key) return { key };

  for (const item of API_ERROR_PATTERNS) {
    const match = normalized.match(item.pattern);
    if (match) {
      return { key: item.key, params: item.params(match) };
    }
  }

  return null;
}

export function getApiErrorMessage(message, t, fallbackKey) {
  const resolved = resolveApiError(message);
  if (resolved) {
    return t(resolved.key, resolved.params || {});
  }
  return t(fallbackKey);
}

export function getRegisterApiErrorMessage(message, t) {
  return getApiErrorMessage(message, t, 'register.error.desc');
}
